/**
 * Search → Results — End-Kund:innen-„Suche & Treffer"-Mockup.
 *
 * Eine premium App-UI-Szene: oben eine gläserne SUCHLEISTE mit Lupe, in die
 * Zeichen für Zeichen eine Suchanfrage getippt wird (echte Typewriter-
 * Animation via raw `onUpdate`-Tween + blinkender Caret). Sobald die Eingabe
 * fertig ist, gleiten 3–4 ERGEBNIS-KARTEN gestaffelt von unten herein — je
 * Karte ein Thumb-Tile (Lucide-Icon auf akzent-getöntem Quadrat), eine
 * Mittelspalte (Name fett + Meta-Zeile gedämpft) und rechts eine
 * Sterne-Bewertung (N Sterne im Akzent, einzeln eingepoppt).
 *
 * Items-Format:  "Name | Meta | rating | iconKey"
 *   z. B. "Trattoria Bella | 600 m · Italienisch | 5 | shop"
 *   rating = ganze Zahl 1..5 (Default 5). iconKey = gültiger ICON_PATHS-Key
 *   (shop, store, truck, taxi, hotel, event, service, heart, euro, pin,
 *   search …); Fallback "store".
 *
 * Inputs:
 *   - text     → die Suchanfrage, die getippt wird (Default "Pizza in der Nähe")
 *   - subtext  → kleines Eyebrow/Label über der Leiste (Default "ALLYVER SUCHE")
 *   - accent   → Akzent-Token
 *   - items    → Ergebniszeilen (s. o.), auf 4 gekappt
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

/** Gültige ICON_PATHS-Keys (s. `_mockHelpers.ICON_PATHS`); Fallback "store". */
const VALID_ICONS = new Set([
  "bolt", "check", "heart", "star", "shop", "taxi", "hotel", "event", "service",
  "chat", "lock", "globe", "gift", "rocket", "chart", "map", "clock", "eye",
  "pin", "shield", "badge", "credit", "flag", "headphones", "zap", "store",
  "truck", "euro", "trending", "award", "search", "message", "phone",
  "smartphone", "flame", "thumbsup", "users",
]);

export const searchTypeResults: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const t = makeTl(clipStartSec);

  const query = inputs.text ?? "Pizza in der Nähe";
  const eyebrow = (inputs.subtext ?? "ALLYVER SUCHE").trim();

  const rawItems = (inputs.items?.length
    ? inputs.items
    : [
        "Trattoria Bella | 600 m · Italienisch | 5 | shop",
        "Pizzeria Romana | 1,2 km · Holzofen | 4 | shop",
        "Da Vinci | 1,8 km · Lieferung | 5 | truck",
        "Mama Mia | 2,1 km · Abholung | 4 | store",
      ]
  ).slice(0, 4);

  const results = rawItems.map((raw) => {
    const [name, meta, ratingRaw, iconRaw] = splitItem(raw);
    const parsedRating = parseInt((ratingRaw ?? "").trim(), 10);
    const rating = Number.isFinite(parsedRating) ? Math.max(1, Math.min(5, parsedRating)) : 5;
    const iconKey = (iconRaw ?? "").trim().toLowerCase();
    return {
      name: (name ?? "").trim(),
      meta: (meta ?? "").trim(),
      rating,
      icon: VALID_ICONS.has(iconKey) ? iconKey : "store",
    };
  });

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Responsive Größen.
  const queryPx = isVertical ? md * 0.58 : md * 0.42;
  const namePx = isVertical ? sm * 0.74 : md * 0.36;
  const metaPx = isVertical ? sm * 0.5 : sm * 0.52;
  const eyebrowPx = isVertical ? sm * 0.42 : sm * 0.44;
  const magSize = isVertical ? 40 : 38;
  const thumbBox = isVertical ? 64 : 72;
  const starSize = isVertical ? 22 : 24;
  const containerMaxW = isVertical ? 900 : 1180;

  const wrapId = `${clipPrefix}-wrap`;
  const eyebrowId = `${clipPrefix}-eye`;
  const barId = `${clipPrefix}-bar`;
  const magId = `${clipPrefix}-mag`;
  const txtId = `${clipPrefix}-q`;
  const caretId = `${clipPrefix}-caret`;
  const cardIds = results.map((_, i) => `${clipPrefix}-card-${i}`);
  const starId = (i: number, j: number) => `${clipPrefix}-c${i}s${j}`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const starsHtml = (i: number, rating: number): string =>
    Array.from({ length: rating }, (_, j) =>
      `<span id="${starId(i, j)}" style="display:inline-flex;color:${accent};filter:drop-shadow(0 0 6px ${withAlpha(accent, 0.55)});">${lucideIcon("star", starSize, accent, 2.2)}</span>`,
    ).join("");

  const card = (i: number, it: (typeof results)[number]): string =>
    `<div id="${cardIds[i]}" style="position:relative;display:flex;align-items:center;gap:${isVertical ? 16 : 20}px;width:100%;padding:${isVertical ? "16px 20px" : "20px 24px"};border-radius:22px;background:linear-gradient(160deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.bg, 0.62)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.12)};border-left:3px solid ${withAlpha(accent, 0.85)};box-shadow:0 14px 38px rgba(0,0,0,0.34), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.08)};backdrop-filter:blur(6px);will-change:transform,opacity;">
      <div style="flex-shrink:0;width:${thumbBox}px;height:${thumbBox}px;border-radius:18px;display:flex;align-items:center;justify-content:center;background:linear-gradient(150deg, ${withAlpha(accent, 0.4)} 0%, ${withAlpha(accent, 0.12)} 100%);border:1px solid ${withAlpha(accent, 0.45)};box-shadow:inset 0 1px 0 ${withAlpha(themeColors.fg, 0.12)};color:${themeColors.fg};">
        ${lucideIcon(it.icon, Math.round(thumbBox * 0.46), themeColors.fg, 2.1)}
      </div>
      <div style="display:flex;flex-direction:column;gap:5px;flex:1;min-width:0;">
        <span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${namePx}px;letter-spacing:-0.4px;color:${themeColors.fg};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(it.name)}</span>
        ${it.meta ? `<span style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${metaPx}px;color:${withAlpha(themeColors.fg, 0.6)};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(it.meta)}</span>` : ""}
      </div>
      <div style="flex-shrink:0;display:flex;align-items:center;gap:${isVertical ? 3 : 4}px;">${starsHtml(i, it.rating)}</div>
    </div>`;

  const cardsHtml = results.map((it, i) => card(i, it)).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:${isVertical ? "100px 56px" : "64px 90px"};overflow:hidden;">
    <div style="display:flex;flex-direction:column;align-items:stretch;gap:${isVertical ? 18 : 22}px;width:100%;max-width:${containerMaxW}px;">
      ${eyebrow ? `<div id="${eyebrowId}" style="display:flex;align-items:center;gap:12px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowPx}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">
        <span style="width:30px;height:2px;background:${accent};display:inline-block;"></span>
        <span>${escapeHtml(eyebrow)}</span>
      </div>` : ""}
      <div id="${barId}" style="display:flex;align-items:center;gap:${isVertical ? 16 : 18}px;width:100%;padding:${isVertical ? "20px 26px" : "22px 30px"};border-radius:9999px;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.18)};box-shadow:0 16px 40px rgba(0,0,0,0.32), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.1)};backdrop-filter:blur(8px);">
        <span id="${magId}" style="display:inline-flex;flex-shrink:0;color:${accent};transform-origin:50% 50%;">${lucideIcon("search", magSize, accent, 2.4)}</span>
        <span style="flex:1;display:inline-flex;align-items:center;min-width:0;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${queryPx}px;letter-spacing:-0.4px;color:${themeColors.fg};white-space:nowrap;overflow:hidden;">
          <span id="${txtId}"></span><span id="${caretId}" style="display:inline-block;flex-shrink:0;width:2px;height:${Math.round(queryPx * 0.92)}px;margin-left:3px;background:${accent};box-shadow:0 0 10px ${withAlpha(accent, 0.7)};"></span>
        </span>
      </div>
      <div style="display:flex;flex-direction:column;gap:${isVertical ? 12 : 14}px;width:100%;">
        ${cardsHtml}
      </div>
    </div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyebrowId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${barId}`, { opacity: 0, y: 18, scale: 0.97, transformOrigin: "50% 50%" }),
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 26, scale: 0.97, transformOrigin: "50% 0%" })),
    ...results.flatMap((it, i) =>
      Array.from({ length: it.rating }, (_, j) => gsapSetLine(`#${starId(i, j)}`, { scale: 0, transformOrigin: "50% 50%" })),
    ),
  ].filter(Boolean).join("\n");

  // Tipp-Animation: exakt das typewriter.ts-Muster (handgeschriebener raw-Tween).
  const safeQuery = JSON.stringify(query);
  const typeDurSec = Math.min(1.6, Math.max(0.7, query.length / 18));
  const typeStart = 0.3;

  const tweens: string[] = [];

  if (eyebrow) tweens.push(t.to(`#${eyebrowId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.1));
  tweens.push(t.to(`#${barId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 0.15));

  // Lupe pulsiert dezent.
  tweens.push(t.to(`#${magId}`, {
    scale: 1.12, duration: 0.6, repeat: Math.max(1, Math.floor(clipDurationSec / 0.6)),
    yoyo: true, ease: "raw:\"sine.inOut\"",
  }, typeStart));

  // Zeichenweises Tippen + blinkender Caret.
  tweens.push(`tl.to({v:0},{v:${query.length},duration:${typeDurSec},ease:"power1.inOut",onUpdate:function(){var v=Math.round(this.targets()[0].v);var el=document.getElementById(${JSON.stringify(txtId)});if(el)el.textContent=${safeQuery}.slice(0,v);}},${(clipStartSec + typeStart).toFixed(4)});`);
  tweens.push(`tl.to(${JSON.stringify("#" + caretId)},{opacity:0,duration:0.001,repeat:Math.max(1,Math.floor(${clipDurationSec * 4})),yoyo:true,repeatDelay:0.25,ease:"none"},${clipStartSec.toFixed(4)});`);

  // Ergebnis-Karten staffeln sich nach Abschluss der Eingabe ein.
  const cardsStart = typeStart + typeDurSec + 0.15;
  const entryWindow = Math.min(1.8, Math.max(0.8, clipDurationSec * 0.45));
  const step = entryWindow / Math.max(results.length, 1);

  results.forEach((it, i) => {
    const at = cardsStart + i * step;
    tweens.push(t.to(`#${cardIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.5)\"" }, at));
    for (let j = 0; j < it.rating; j++) {
      tweens.push(t.to(`#${starId(i, j)}`, { scale: 1, duration: 0.32, ease: "raw:\"back.out(3)\"" }, at + 0.22 + j * 0.06));
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
